import { useMutation, useQueryClient } from "react-query";

import axios from "axios";

import { geoMapServiceEndpoint } from "../../../../../api/project";
import { PROJECT_LAYER_LIST_QUERY_KEYS } from "../../../layers/queries";
import {
  ORGANIZATION_SHAPEFILES_QUERY_KEYS,
  PROJECT_SHAPEFILES_QUERY_KEYS
} from "../../queries";

export interface UploadGeoTiffParams {
  files: File[];
  visibility: string;
  projectId: string;
  shapefileNodeId?: string;
  thickness?: string;
  /** Aborts the request when the user cancels the upload. */
  signal?: AbortSignal;
}

export interface IUploadProjectGeoTiffMutationParams {
  onMutate?: () => void;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onSettled?: () => void;
  projectId?: string;
}

export function uploadGeoTiff(
  files: File[],
  visibility: string,
  projectId: string,
  shapefileNodeId: string,
  thickness: string,
  signal?: AbortSignal
) {
  const formData = new FormData();
  for (const file of files) {
    formData.append("payload", file);
  }

  formData.append("projectId", projectId);
  formData.append("shapefileNodeId", shapefileNodeId ?? "");
  formData.append("thickness", thickness);
  formData.append("visibility", visibility);
  return axios.post(`${geoMapServiceEndpoint}/geo-tiff/upload`, formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    },
    signal
  });
}

export function useUploadProjectGeoTiffMutation({
  onError,
  onSuccess,
  onSettled,
  onMutate,
  projectId
}: IUploadProjectGeoTiffMutationParams) {
  const queryClient = useQueryClient();
  return useMutation(
    async (params: UploadGeoTiffParams) => {
      for (const file of params.files) {
        await uploadGeoTiff(
          [file],
          params.visibility,
          projectId,
          params.shapefileNodeId,
          params.thickness,
          params.signal
        );
      }
    },
    {
      onMutate: onMutate && onMutate,
      onSuccess: () => {
        if (!projectId) {
          queryClient.invalidateQueries({
            queryKey: [ORGANIZATION_SHAPEFILES_QUERY_KEYS.organizationShapefiles]
          });
        } else {
          queryClient.invalidateQueries({
            queryKey: [PROJECT_SHAPEFILES_QUERY_KEYS.currentProjectShapefiles, projectId]
          });
          queryClient.invalidateQueries({
            queryKey: [PROJECT_LAYER_LIST_QUERY_KEYS.currentProjectLayerList, projectId]
          });
        }

        onSuccess && onSuccess();
      },
      onError: (error) => {
        onError && onError(error);
      },
      onSettled: onSettled && onSettled
    }
  );
}
